import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCartPricingRefreshing } from '../../contexts/CartPricingContext';
import useFreeShippingDisplay from '../../hooks/useFreeShippingDisplay';
import * as types from '../../types';
import { useCartCheckout } from '../CartSummary/CartActions';
import useHardwareFirstTotals from './useHardwareFirstTotals';
import styles from './HardwareFirstOrderSummary.module.scss';

interface HardwareFirstOrderSummaryProps {
  onCheckOut: (checkoutCart: types.Cart, cartPricing: types.CartPricing) => void;
}

/**
 * The hardware-first receipt: what the hardware lists at, what came off it, and the pre-tax
 * subtotal, followed by the returns and support reassurances that sit under the button in the
 * design. Tax lands at checkout, so this total is a subtotal rather than a final amount. Desktop
 * checks out from the button here; mobile hides it and uses the pinned bar instead.
 */
export default function HardwareFirstOrderSummary({ onCheckOut }: HardwareFirstOrderSummaryProps) {
  const { t } = useTranslation();
  const { isFreeShipping, shippingPrice } = useFreeShippingDisplay();
  const { trackerCount, hardwareInCents, promoDiscountInCents, multiDeviceDiscountInCents, totalInCents, formatPrice } =
    useHardwareFirstTotals();
  const checkout = useCartCheckout(onCheckOut);
  const refreshing = useCartPricingRefreshing();
  const pendingValue = classNames(styles.rowValue, { [styles.pending]: refreshing });

  return (
    <div className={styles.summary}>
      <div className={styles.card}>
        <h2 className={styles.title}>{t('hardware_first_cart_order_summary')}</h2>
        <div className={styles.rows} aria-busy={refreshing}>
          <div className={styles.row}>
            <span className={styles.rowLabel}>
              {trackerCount === 1
                ? t('hardware_first_cart_tracker_one')
                : t('hardware_first_cart_tracker_other', { count: trackerCount })}
            </span>
            <span className={styles.rowValue}>{formatPrice(hardwareInCents)}</span>
          </div>
          {promoDiscountInCents > 0 && (
            <div className={classNames(styles.row, styles.discountRow)}>
              <span className={styles.rowLabel}>{t('discount')}</span>
              <span className={pendingValue}>
                {t('negative_amount', { amount: formatPrice(promoDiscountInCents) })}
              </span>
            </div>
          )}
          {multiDeviceDiscountInCents > 0 && (
            <div className={classNames(styles.row, styles.discountRow)}>
              <span className={styles.rowLabel}>{t('hardware_first_cart_multi_device_discount')}</span>
              <span className={pendingValue}>
                {t('negative_amount', { amount: formatPrice(multiDeviceDiscountInCents) })}
              </span>
            </div>
          )}
          <div className={styles.row}>
            <span className={styles.rowLabel}>{t('hardware_first_cart_ground_shipping')}</span>
            <span className={styles.rowValue}>{isFreeShipping ? t('cart_item_v2.price_free') : shippingPrice}</span>
          </div>
          <div className={styles.divider} />
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>{t('subtotal')}</span>
            <span className={classNames(styles.totalValue, { [styles.pending]: refreshing })}>
              {formatPrice(totalInCents)}
            </span>
          </div>
          <p className={styles.taxNote}>{t('hardware_first_cart_taxes_at_checkout')}</p>
        </div>
        <button className={styles.checkoutButton} onClick={checkout} type="button">
          {t('checkout')}
        </button>
      </div>
      <div className={styles.reassurance}>
        <span className={styles.reassuranceTitle}>{t('hardware_first_cart_risk_free_title')}</span>
      </div>
      <div className={styles.reassurance}>
        <span className={styles.reassuranceTitle}>{t('hardware_first_cart_questions_title')}</span>
        <span className={styles.reassuranceBody}>{t('hardware_first_cart_questions_body')}</span>
      </div>
    </div>
  );
}
